import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  type ListOrgFunctionsItem,
  type OrgFunctionKind,
  type AddOrgFunctionRequest,
  type UpdateOrgFunctionRequest,
  type AddOrgFunctionResponse
} from '@bliks-io/contracts';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import {
  useCreateOrgFunction,
  useUpdateOrgFunction,
  useDeleteOrgFunction
} from '@/api/mutations/useOrgFunctions';
import { useAuthStore } from '@/stores/auth-store';
import {
  createActionsColumn,
  createColumnHeader,
  renderFallback,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { OrganizationTabsNav } from '@/components/organization/OrganizationTabsNav';
import { orgFunctionCopyPrefix, orgFunctionDetailRoute } from '@/lib/orgFunctionRoutes';
import { OrgFunctionFormDialog } from './components/OrgFunctionFormDialog';
import {
  buildOrgFunctionNameColumn,
  buildOrgFunctionLeadColumn,
  buildOrgFunctionMemberCountColumn
} from './utils/orgFunctionColumns';
import {
  createEntityListPage,
  type EntityListPageDescriptor
} from '@/components/entity-list/EntityListPage';

/**
 * Storage stays one unified OrgFunction collection — only the presentation is
 * split into a „Teams" and a „Funktionen" page (ADR 0004), so the kind is fixed
 * per route and never user-selectable.
 */
export function orgFunctionCatalogDescriptor(
  kind: OrgFunctionKind
): EntityListPageDescriptor<
  ListOrgFunctionsItem,
  AddOrgFunctionRequest,
  UpdateOrgFunctionRequest,
  AddOrgFunctionResponse
> {
  const copyPrefix = orgFunctionCopyPrefix(kind);

  return {
    documentTitleKey: kind === 'team' ? 'pageTitles.teams' : 'pageTitles.functions',
    i18nPrefix: copyPrefix,
    icon: ENTITY_ICONS[kind],
    TabsNav: OrganizationTabsNav,
    tableId: kind === 'team' ? 'teams' : 'functions',
    enableGlobalFilter: true,
    useRows: () => useOrgFunctions(kind),
    useMutations: () => ({
      create: useCreateOrgFunction(),
      update: useUpdateOrgFunction(),
      remove: useDeleteOrgFunction()
    }),
    useColumns: ({ openEdit, openDelete }) => {
      const { t } = useTranslation();
      const slug = useAuthStore().slug();
      const navigate = useNavigate();

      const handleView = useCallback(
        (orgFunction: ListOrgFunctionsItem) => {
          navigate({
            to: orgFunctionDetailRoute(kind),
            params: { slug, orgFunctionId: orgFunction._id }
          });
        },
        [navigate, slug]
      );

      return useMemo<DataTableColumnDef<ListOrgFunctionsItem>[]>(
        () => [
          buildOrgFunctionNameColumn(t, slug),
          {
            id: 'department',
            accessorFn: (row) => row.department?.abbreviation ?? row.department?.name,
            meta: { label: t('organization.orgFunctions.department') },
            header: createColumnHeader(t('organization.orgFunctions.department')),
            cell: ({ row }) =>
              renderFallback(row.original.department?.abbreviation ?? row.original.department?.name)
          },
          buildOrgFunctionLeadColumn(t),
          buildOrgFunctionMemberCountColumn(t),
          createActionsColumn<ListOrgFunctionsItem>({
            onEdit: openEdit,
            onDelete: (orgFunction) => openDelete(orgFunction._id),
            onView: handleView
          })
        ],
        [t, slug, openEdit, openDelete, handleView]
      );
    },
    renderFormDialog: ({ open, onOpenChange, entity, onSubmit }) => (
      <OrgFunctionFormDialog
        open={open}
        onOpenChange={onOpenChange}
        onSubmit={onSubmit}
        kind={kind}
        orgFunction={entity}
      />
    ),
    deleteEntityPhraseKey: `organization.orgFunctions.deletePhrase.${kind}`
  };
}

export const TeamCatalogPage = createEntityListPage(orgFunctionCatalogDescriptor('team'));
export const FunctionCatalogPage = createEntityListPage(orgFunctionCatalogDescriptor('role'));
